import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCurrentTenant, updateBranding, type Branding } from "@/api/tenants";
import { Button, Card } from "@/components";
import { useLang } from "@/i18n/LanguageContext";

export function BrandingPage() {
  const qc = useQueryClient();
  const { t } = useLang();
  const tenant = useQuery({ queryKey: ["tenant-current"], queryFn: ({ signal }) => fetchCurrentTenant(signal) });

  const [accent, setAccent] = useState("#EF9F27");
  const [accentDark, setAccentDark] = useState("#BA7517");
  const [brandName, setBrandName] = useState("");
  const [locale, setLocale] = useState<"nl" | "en">("nl");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenant.data) {
      const b = (tenant.data.settings?.branding as Branding) ?? {};
      setAccent(b.accent ?? "#EF9F27");
      setAccentDark(b.accent_dark ?? "#BA7517");
      setBrandName(b.brand_name ?? "");
      setLocale(((tenant.data.settings?.locale as "nl" | "en") ?? "nl"));
    }
  }, [tenant.data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => updateBranding({ accent, accent_dark: accentDark, brand_name: brandName, locale }),
    onSuccess: () => {
      setSaved(true);
      setError(null);
      qc.invalidateQueries({ queryKey: ["tenant-current"] }); // re-applies colours live
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Opslaan mislukt"),
  });

  return (
    <section>
      <div className="wrap">
        <div className="toolbar">
          <div>
            <p className="eyebrow">{t("Huisstijl", "Branding")}</p>
            <h2 style={{ fontSize: 30 }}>{t("Huisstijl van je organisatie", "Your organisation's branding")}</h2>
          </div>
        </div>

        <div className="editor-grid">
          <Card>
            <p className="card-num">{t("INSTELLINGEN", "SETTINGS")}</p>
            {error && <div className="form-error">{error}</div>}
            <div className="field">
              <label>{t("Merknaam", "Brand name")}</label>
              <input value={brandName} onChange={(e) => { setBrandName(e.target.value); setSaved(false); }} placeholder="Acme BV" />
            </div>
            <div className="field">
              <label>{t("Accentkleur", "Accent colour")}</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="color" value={accent} onChange={(e) => { setAccent(e.target.value); setSaved(false); }} style={{ width: 48, height: 38, padding: 0, border: "1px solid var(--line)", borderRadius: 4 }} />
                <input value={accent} onChange={(e) => { setAccent(e.target.value); setSaved(false); }} style={{ width: 120 }} />
              </div>
            </div>
            <div className="field">
              <label>{t("Accent (hover/donker)", "Accent (hover/dark)")}</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="color" value={accentDark} onChange={(e) => { setAccentDark(e.target.value); setSaved(false); }} style={{ width: 48, height: 38, padding: 0, border: "1px solid var(--line)", borderRadius: 4 }} />
                <input value={accentDark} onChange={(e) => { setAccentDark(e.target.value); setSaved(false); }} style={{ width: 120 }} />
              </div>
            </div>
            <div className="field">
              <label>{t("Standaardtaal", "Default language")}</label>
              <select value={locale} onChange={(e) => { setLocale(e.target.value as "nl" | "en"); setSaved(false); }}>
                <option value="nl">Nederlands</option>
                <option value="en">English</option>
              </select>
            </div>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? t("Opslaan…", "Saving…") : saved ? t("Opgeslagen ✓", "Saved ✓") : t("Opslaan", "Save")}</Button>
          </Card>

          <Card>
            <p className="card-num">{t("VOORBEELD", "PREVIEW")}</p>
            <p style={{ color: "var(--grey-text)", fontSize: 13, marginBottom: 14 }}>
              {t("De accentkleur wordt direct toegepast.", "The accent colour is applied immediately.")}
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <button className="btn btn-primary" type="button">{brandName || "OnboardForge"}</button>
              <button className="btn btn-outline" type="button">{t("Knop", "Button")}</button>
              <span className="eyebrow">{t("Voorbeeld-label", "Sample label")}</span>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}
